import React from "react";

/**
 * A wrapper for React.lazy that retries the import once if it fails.
 * This helps mitigate chunk loading errors that occur after a new deployment.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const getRetryStatus = () => {
      try {
        return window.sessionStorage.getItem("last-retry-status");
      } catch {
        return null;
      }
    };

    const setRetryStatus = (status: string) => {
      try {
        window.sessionStorage.setItem("last-retry-status", status);
      } catch {
        // Ignore storage errors
      }
    };

    // Attempt the import a few times with backoff before treating it as a
    // hard failure. On slow/flaky connections the first fetch often times out
    // but a quick retry succeeds — this avoids bouncing users to an error
    // screen (or a full reload) for a transient network blip.
    const importWithRetries = async (attempts = 3, delayMs = 600) => {
      let lastErr: any;
      for (let i = 0; i < attempts; i++) {
        try {
          return await componentImport();
        } catch (err) {
          lastErr = err;
          // No point retrying while offline — fail fast to the boundary.
          if (typeof navigator !== "undefined" && navigator.onLine === false) {
            throw err;
          }
          if (i < attempts - 1) {
            await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
          }
        }
      }
      throw lastErr;
    };

    try {
      const component = await importWithRetries();
      // If import succeeds, clear any previous retry flag to allow future retries if needed
      try {
        window.sessionStorage.removeItem("last-retry-status");
      } catch {
        // Ignore
      }
      return component;
    } catch (error: any) {
      // While offline a reload cannot help — let the error reach the boundary
      // so it can show the friendly "no connection" screen.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw error;
      }
      const pageHasAlreadyBeenRetried = getRetryStatus();
      // Match case-insensitively — Vite emits "Failed to fetch dynamically
      // imported module" (capital F) and other bundlers vary in casing.
      const message = (error?.message || "").toLowerCase();
      const isChunkError =
        message.includes("loading chunk") ||
        error?.name === "ChunkLoadError" ||
        message.includes("failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("importing a module script failed") ||
        // Vite throws this when a lazy route's CSS chunk 404s after a redeploy
        // (the exact "Unable to preload CSS for /assets/Landing-…css" error).
        // Without this branch the stale client never self-heals and the route
        // white-screens to the error boundary.
        message.includes("unable to preload css") ||
        message.includes("unable to preload") ||
        /loading chunk\s+\S+\s+failed/i.test(message);

      // Only retry if it looks like a chunk load error and we haven't retried yet
      if (isChunkError && !pageHasAlreadyBeenRetried) {
        setRetryStatus("true");
        window.location.reload();
        // Return a promise that never resolves while the page is reloading
        return new Promise(() => {});
      }

      // If not a chunk error or we already retried, let it bubble up
      throw error;
    }
  });
}
